import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, CookingPot, Sprout, Users, Infinity as Inf } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { FadeUp } from "../components/site/motion";
import { VisionNote } from "../components/site/Badges";
import { Container, SectionHead } from "../components/sections/SectionHead";
import { M } from "../data/site";

const PATHS = [
  { to: "/advanced-learning/honestgrow-university", icon: GraduationCap, title: "HonestGrow™ University", who: "For parents & caregivers", text: "Practical guidance for a healthier home — from cooking and nutrition to wellness, routines and everyday life.", img: M("university.jpg") },
  { to: "/advanced-learning/little-chefs-academy", icon: CookingPot, title: "Little Chefs Academy", who: "For kids 4–12", text: "Fun, age-appropriate cooking and nutrition lessons that build confidence, creativity and lifelong healthy habits.", img: M("littlechefs.jpg") },
];

const STAGES = [
  { icon: Sprout, t: "Everyday help", d: "Honesti starts with tonight’s dinner." },
  { icon: Users, t: "Learning together", d: "Short lessons the whole family can share." },
  { icon: Inf, t: "Lifelong habits", d: "Skills that last well beyond the kitchen." },
];

export default function Learning() {
  return (
    <div data-testid="learning-page">
      <PageHero badge="Product vision · Concept preview" eyebrow="Advanced learning" img={M("c_garden.jpg")} alt="Honesti planting a seedling with a child"
        lines={[{ text: "Learning that" }, { text: "grows with", className: "text-grad" }, { text: "your family.", className: "text-grad" }]}
        intro="Beyond the kitchen, Honesti is envisioned as a patient teacher — for grown-ups building healthier routines and for kids discovering the joy of cooking." />
      <Container testId="learning-paths">
        <SectionHead eyebrow="Two paths" title={<>Choose where to <span className="text-grad">explore</span></>} right={<VisionNote />} />
        <div className="grid gap-6 lg:grid-cols-2">
          {PATHS.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.12}>
              <Link to={p.to} data-testid={`learning-path-${i}`} className="group relative block h-[520px] overflow-hidden rounded-[2rem] border border-cyan-400/25 transition-[box-shadow,border-color] duration-500 hover:border-cyan-300/70 hover:shadow-[0_0_50px_rgba(34,211,238,0.28)]">
                <img src={p.img} alt={p.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
                <div className="absolute right-6 top-6 grid h-12 w-12 place-items-center rounded-full border border-white/40 bg-ink/40 backdrop-blur transition-[transform,background-color] duration-500 group-hover:rotate-45 group-hover:bg-cyan-400 group-hover:text-ink"><ArrowUpRight className="h-5 w-5" /></div>
                <div className="absolute inset-x-0 bottom-0 p-7 lg:p-10">
                  <p className="eyebrow">{p.who}</p>
                  <div className="mt-3 flex items-center gap-3"><p.icon className="h-8 w-8 text-cyan-200" strokeWidth={1.4} /><h2 className="font-display text-3xl font-bold text-white lg:text-4xl">{p.title}</h2></div>
                  <p className="mt-3 max-w-lg text-sm text-slate-200 md:text-base">{p.text}</p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
        <div className="mt-16 grid gap-4 md:grid-cols-3">
          {STAGES.map((s, i) => (
            <FadeUp key={s.t} delay={i * 0.1}>
              <div className="border-t border-cyan-400/30 pt-6"><s.icon className="h-7 w-7 text-cyan-300" strokeWidth={1.4} /><p className="mt-4 font-display text-2xl font-semibold text-white">{s.t}</p><p className="mt-1 text-sm text-slate-300">{s.d}</p></div>
            </FadeUp>
          ))}
        </div>
      </Container>
    </div>
  );
}
